<script setup lang="ts">
  import SingleEmployeeSelect from '@/components/team-device/SingleEmployeeSelect.vue'
  import { Employee } from '@/classes/Employee'
  import { ref, watch, WatchStopHandle } from 'vue'
  import BaseModal from './BaseModal.vue'

  const modal = ref<InstanceType<typeof BaseModal>>()
  const employeesToChooseFrom = ref<Employee[]>([])
  const selectedEmployee = ref<Employee | null>(null)
  const title = ref('')
  const prompt = ref('')
  const statusMessage = ref('')

  async function selectEmployee(
    employees: Employee[],
    customTitle?: string,
    customPrompt?: string,
    customStatusMessage?: string,
  ): Promise<Employee | null> {
    employeesToChooseFrom.value = employees
    title.value = customTitle ?? ''
    prompt.value = customPrompt ?? ''
    statusMessage.value = customStatusMessage ?? ''
    modal.value?.open()
    return new Promise((resolve) => {
      const watcherList: WatchStopHandle[] = []
      const resolveAndStop = (employee: Employee | null) => {
        resolve(employee)
        watcherList.forEach((stop) => stop())
        reset()
      }
      watcherList.push(
        watch(
          selectedEmployee,
          (selectedEmployee) =>
            selectedEmployee &&
            handleEmployeeSelected(selectedEmployee, resolveAndStop),
        ),
      )
      // Listen to the modal's close event instead of watching isOpen
      const handleModalClose = () => handleModalClosed(resolveAndStop)
      modal.value?.onWillDismiss().then(handleModalClose)
    })
  }

  function reset() {
    selectedEmployee.value = null
    title.value = ''
    prompt.value = ''
    statusMessage.value = ''
    employeesToChooseFrom.value = []
  }

  function handleEmployeeSelected(
    employee: Employee,
    resolveAndStop: (employee: Employee | null) => void,
  ) {
    modal.value?.close()
    resolveAndStop(employee)
  }

  function handleModalClosed(
    resolveAndStop: (employee: Employee | null) => void,
  ) {
    selectedEmployee.value = null
    resolveAndStop(null)
  }

  function employeeWasClicked(employee: Employee) {
    selectedEmployee.value = employee
  }

  defineExpose({
    selectEmployee,
  })
</script>
<template>
  <base-modal
    ref="modal"
    appium="employee-selection-modal"
    :appium-state="modal?.isOpen ? 'open' : 'closed'"
  >
    <template #title>
      {{ title }}
    </template>
    <template #default>
      <h2 v-show="prompt.length > 0" class="text-center">{{ prompt }}</h2>
      <span v-show="statusMessage.length > 0">{{ statusMessage }}</span>
      <div class="flex flex-col">
        <template
          v-for="(employee, index) in employeesToChooseFrom"
          :key="employee.id"
        >
          <div class="px-6 h-full">
            <single-employee-select
              :employee="employee"
              @click="() => employeeWasClicked(employee)"
            >
            </single-employee-select>
            <hr v-if="index < employeesToChooseFrom.length - 1" />
          </div>
        </template>
      </div>
    </template>
  </base-modal>
</template>
